<template>
  <v-container class="container-content my-4">
    <v-card color="primary" class="fipa--header" tile>
      <div class="d-flex ml-auto">
        <v-btn
          v-if="$allowedMutation(allowedMutations, 'horseActivateSportLicence')"
          variant="text"
          color="white"
          class="btn-with-icon mx-2"
          aria-hashpopup="true"
          :loading="mutationLoading"
          data-test="activateLicence"
          @click="horseLicenceDialog = true"
        >
          <div class="bg-edit btn-icon-with-text">
            <v-icon size="small" aria-hidden="false" class="text-white">
              $iconPlus
            </v-icon>
          </div>
          <span class="text-white">{{
            $t(
              "horses:HorseProfilePage.button.ActivateLicence.label",
              "Activate licence",
              "Label for activating horse licence"
            )
          }}</span>
        </v-btn>
        <v-tooltip
          v-if="
            $allowedMutation(allowedMutations, [
              'horsePurchaseSeasonSportLicence',
              'horsePurchaseTemporarySportLicence',
              'horseApplicationFeiSeasonLicence',
              'horsePurchaseLifetimeSportLicence'
            ])
          "
          location="bottom"
        >
          <template #activator="{ props }">
            <v-btn
              variant="text"
              color="white"
              v-bind="props"
              class="btn-with-icon"
              :loading="mutationLoading"
              @click="$emit('openPurchases')"
            >
              <div class="bg-edit btn-icon-with-text">
                <v-icon size="small" aria-hidden="false" class="text-white">
                  $iconPlus
                </v-icon>
              </div>
              <span class="text-white">{{
                $t(
                  "horses:HorseProfilePage.button.BuyLicence.label",
                  "Buy licence",
                  "Label for horse licence purchases"
                )
              }}</span>
            </v-btn>
          </template>
          <span>{{
            $t(
              "horses:HorseProfilePage.button.BuyLicence.tooltip",
              "Buy licence",
              "Tooltip for horse licence purchases"
            )
          }}</span>
        </v-tooltip>
      </div>
    </v-card>

    <v-data-table
      :headers="headers"
      class="entsys--table"
      :items="licences"
      :loading="$apollo.queries.licences.loading"
      disable-sort
      data-test="licencesTable"
      :items-per-page="-1"
      hide-default-footer
      :hide-default-header="$vuetify.display.smAndDown"
    >
      <template #item="{ item }">
        <tr>
          <td>{{ $dayjs.date(item.createdAt) }}</td>
          <td>{{ item.year }}</td>
          <td>
            {{ productCodenameToName(item.productCodename) }}
            <span
              v-if="item.productCodename == 'HORSE_SEASON_FEI_LICENCE'"
              class="text-secondary"
            >
              ({{
                $t(
                  "common:discipline." +
                    getDisciplineFromMaster(item.disciplineId, "name")
                )
              }})
            </span>
            <div>
              {{ $dayjs.date(item.validFrom) }} -
              {{ $dayjs.date(item.validUntil) }}
            </div>
          </td>
          <td>
            <span v-if="item.payable">{{
              masterCurrency(item.payable.amountCents)
            }}</span>
          </td>
          <td class="text-right">
            <v-tooltip
              v-if="
                $allowedMutation(
                  item.allowedMutations,
                  'horseSportLicenceDeactivate'
                )
              "
              location="bottom"
            >
              <template #activator="{ props }">
                <v-btn-icon
                  icon
                  v-bind="props"
                  class="bg-remove"
                  aria-hashpopup="true"
                  size="small"
                  :disabled="Boolean(mutationId || mutationLoading)"
                  :loading="mutationId == item.id"
                  :aria-label="$t('common:deleteButton.alt')"
                  data-test="licenceDeactivate"
                  @click="licenceDeactivate(item.id)"
                >
                  <v-icon color="white"> $iconTimesCircle </v-icon>
                  <template #loader>
                    <v-progress-circular
                      color="white"
                      size="21"
                      indeterminate
                    />
                  </template>
                </v-btn-icon>
              </template>
              <span>{{
                $t(
                  "horses:horseProfilePage.licencesSection.horseSportLicenceDeactivate.hint"
                )
              }}</span>
            </v-tooltip>
            <v-tooltip
              v-if="
                $allowedMutation(
                  item.allowedMutations,
                  'horseSportLicenceDiscard'
                )
              "
              location="bottom"
            >
              <template #activator="{ props }">
                <v-btn-icon
                  icon
                  v-bind="props"
                  class="bg-remove"
                  aria-hashpopup="true"
                  :aria-label="
                    $t(
                      'horses:HorseProfilePage.deactivateHorseLicenceButton.alt'
                    )
                  "
                  size="small"
                  :disabled="Boolean(mutationId || mutationLoading)"
                  :loading="mutationId == item.id"
                  data-test="licenceDiscard"
                  @click="licenceDiscard(item)"
                >
                  <v-icon color="white"> $iconTrash </v-icon>

                  <template #loader>
                    <v-progress-circular
                      color="white"
                      size="21"
                      indeterminate
                    />
                  </template>
                </v-btn-icon>
              </template>
              <span>{{
                $t(
                  "horses:horseProfilePage.licencesSection.horseSportLicenceDiscard.hint"
                )
              }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <AppDialogContent
      v-if="licencePurchasable"
      v-model="horseLicenceDialog"
      :persistent="mutationLoading"
      :disabled-submit="disabledSubmit || mutationLoading"
      @submit-form="horseLicenceStatus('horseActivateSportLicence')"
    >
      <template #header>
        {{ $t("horses:horseProfilePage.activateLicence.Modal.header") }}
        <br />
      </template>
      <div>
        {{ $t("horses:horseProfilePage.activateLicence.Modal.explainer") }}
      </div>
      <br />
      <AppSelect
        v-model="form.licenceCategoryCodename"
        data-test="horseLicenceCategories"
        :input-props="{
          label: $t(
            'horses:horseProfilePage.activateLicence.licenceTypeSelect.header'
          ),
          items: selectiveLicenses
        }"
      />
      <template v-if="form.licenceCategoryCodename === 'TEMPORARY'">
        <h4 class="my-2">
          {{
            $t(
              "horses:horseProfilePage.activateLicence.validFromValidUntil.header"
            )
          }}
        </h4>
        <v-row dense>
          <v-col>
            <AppDatePicker
              v-model="form.validFrom"
              :min="minFrom"
              :max="maxFrom"
              :label="
                $t('horses:horseProfilePage.activateLicence.validFrom.label')
              "
              :hint="
                $t('horses:horseProfilePage.activateLicence.validFrom.hint')
              "
            />
          </v-col>
          <v-col>
            <AppDatePicker
              v-model="form.validUntil"
              :min="form.validFrom"
              :disabled="!form.validFrom"
              :max="maxFrom"
              :label="
                $t('horses:horseProfilePage.activateLicence.validUntil.label')
              "
              :hint="
                $t('horses:horseProfilePage.activateLicence.validUntil.hint')
              "
            />
          </v-col>
        </v-row>
      </template>

      <template v-if="form.licenceCategoryCodename == 'FEI'">
        <br />
        <AppSelectDiscipline v-model="form.disciplineId" exclude-non-fei />

        <div>
          <b>{{
            $t(
              "horses:horseProfilePage.licencePayment.FEIlicence.season.label",
              "Select season"
            )
          }}</b>
        </div>

        <v-radio-group v-model="form.year" mandatory>
          <v-radio
            v-for="possibleValue in possibleSeasons"
            :key="possibleValue.value"
            :value="possibleValue.value"
          >
            <template #label>
              {{ possibleValue.text }}
            </template>
          </v-radio>
        </v-radio-group>
        <div class="text-muted">
          {{
            $t(
              "horses:horseProfilePage.licencePayment.FEIlicence.season.hint",
              "FEI registration requires selection of season. Before 1st of November, only current year can be selected. From 1st of November next year is also available."
            )
          }}
        </div>
      </template>

      <br />
    </AppDialogContent>
  </v-container>
</template>
<script>
import gql from "fraql";
import { useCurrency } from "@composables/masterdata/config";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useProductCodenameHelpers } from "@composables/product_codename";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppSelect from "@AppSelects/AppSelect.vue";
import AppDatePicker from "@elements/AppDatePicker.vue";
import AppSelectDiscipline from "@AppSelects/AppSelectDiscipline.vue";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";

import { orderBy } from "lodash-es";
//import AppShowPaymentStatus from "@/components/elements/AppShows/AppShowPaymentStatus";

export default {
  name: "HorseLicences",
  components: {
    AppDialogContent,
    AppSelect,
    AppDatePicker,
    AppSelectDiscipline
  },
  props: {
    horseId: { type: String, required: true },
    allowedMutations: { type: Array, required: true }
  },
  emits: ["openPurchases", "refetch"],
  setup() {
    const productCodenameHelpers = useProductCodenameHelpers();
    return {
      ...getFederationConfiguration(),
      ...useCurrency(),
      ...useDisciplineHelper(),
      ...productCodenameHelpers
    };
  },
  data() {
    return {
      form: {
        licenceCategoryCodename: null,
        validFrom: null,
        validUntil: null,
        disciplineId: null,
        year: null
      },
      horseLicenceDialog: false,
      licences: [],
      mutationLoading: false,
      mutationId: null
    };
  },
  computed: {
    possibleSeasons() {
      const currYear = Number(this.$dayjs().format("YYYY"));
      return [
        { text: currYear, value: currYear },
        { text: currYear + 1, value: currYear + 1 }
      ];
    },
    licencePurchasable() {
      return (
        this.$allowedMutation(
          this.allowedMutations,
          "horseActivateSportLicence"
        ) ||
        this.$allowedMutation(
          this.allowedMutations,
          "horsePurchaseTemporarySportLicence"
        )
      );
    },
    minFrom() {
      return this.$dayjs().subtract(1, "year").format("YYYY-MM-DD");
    },
    maxFrom() {
      return this.$dayjs().add(1, "year").format("YYYY-MM-DD");
    },
    disabledSubmit() {
      switch (this.form.licenceCategoryCodename) {
        case "TEMPORARY":
          return !this.form.validFrom || !this.form.validUntil;
        case "SEASON":
          return false;
        case "FEI":
          return !this.form.disciplineId;
        case "LIFETIME":
          return false;
        default:
          return true;
      }
    },
    headers() {
      return [
        {
          title: this.$t(
            "horses:HorseProfilePage.licences.Issued.label",
            "Issued",
            "Label for horse licence records in horse profile, shows date."
          )
        },
        {
          title: this.$t(
            "horses:HorseProfilePage.licences.Season.label",
            "Season",
            "Label for horse licence records in horse profile"
          )
        },
        {
          title: this.$t(
            "horses:HorseProfilePage.licences.Type.label",
            "Type",
            "Label for horse licence records in horse profile"
          )
        },
        {
          title: this.$t(
            "horses:HorseProfilePage.licences.Price.label",
            "Price",
            "Label for horse licence records in horse profile"
          )
        },
        { title: "" }
      ];
    },
    selectiveLicenses() {
      return [
        {
          text: this.$t(
            "horses:horseProfilePage.licencePayment.Modal.label",
            "Season"
          ),
          value: "SEASON"
        },
        {
          text: this.$t(
            "horses:horseProfilePage.HorseTemporaryLicence.Modal.product.label",
            "Temporary"
          ),
          value: "TEMPORARY"
        },
        {
          text: this.$t(
            "horses:horseProfilePage.activateLicence.licenceType.FEI.name",
            "FEI licence"
          ),
          value: "FEI"
        },
        {
          text: this.$t(
            "horses:horseProfilePage.activateLicence.licenceType.lifetimeLicence.name",
            "Lifetime licence"
          ),
          value: "LIFETIME"
        }
      ];
    }
  },
  watch: {
    "form.validFrom"(newVal) {
      if (!this.form.validUntil && newVal) {
        this.form.validUntil = this.$dayjs(newVal)
          .add(
            this.masterConfiguration.horseTemporaryLicenceDefaultLength,
            "days"
          )
          .format("YYYY-MM-DD");
      }
    }
  },
  apollo: {
    licences: {
      query: gql`
        query horseSportLicences($id: ID!) {
          horse(id: $id) {
            id
            sportLicences {
              id
              year
              payable {
                id
                amountCents
              }
              validFrom
              validUntil
              productCodename
              disciplineId
              createdAt
              allowedMutations
            }
          }
        }
      `,
      variables() {
        return { id: this.horseId };
      },
      update({ horse }) {
        return orderBy(horse.sportLicences, "createdAt", "desc");
      }
    }
  },
  methods: {
    horseLicenceStatus(mutation) {
      this.mutationLoading = true;
      switch (this.form.licenceCategoryCodename) {
        case "SEASON":
          this.form.validFrom = null;
          this.form.validUntil = null;
          break;
      }
      this.$apollo
        .mutate({
          mutation: gql`
            mutation ${mutation}(
                $horseId: ID! 
                $licenceCategoryCodename: String!
                $validFrom: Date,
                $validUntil: Date,
                $disciplineId: ID 
                $year: Int
              ) {
              ${mutation}(
                input: {
                  horseId: $horseId
                  licenceCategoryCodename: $licenceCategoryCodename
                  validFrom: $validFrom
                  validUntil:  $validUntil
                  disciplineId: $disciplineId
                  year: $year
                }
              ) {
                horse {
                  id
                  currentSeasonLicenceValid
                  licenceNumber
                  licenceNumberStatus
                  feiid
                  feiPassportExpirationDate
                  allowedMutations
                  currentSportLicences {
                    id
                    licenceCategoryCodename
                    disciplineId
                  }
                  sportLicences {
                    id
                    validFrom
                    validUntil
                    productCodename
                    allowedMutations
                  }
                } 
              }
            }
            `,
          variables: {
            horseId: this.horseId,
            licenceCategoryCodename: this.form.licenceCategoryCodename,
            validFrom: this.form.validFrom,
            validUntil: this.form.validUntil,
            disciplineId: this.form.disciplineId,
            year: this.form.year
          }
        })
        .then(() => {
          //this.$emit("refetch");
          this.form = {
            licenceCategoryCodename: null,
            validFrom: null,
            validUntil: null,
            disciplineId: null
          };
          this.horseLicenceDialog = false;
          this.mutationLoading = false;
          // Doesn't work in refetchQueries for some reason. Maybe due to manual?
          this.$apollo.queries.licences.refetch();
          this.$root.$snackbar("success", { text: "Activated licence!" });
        })
        .catch(err => {
          this.mutationLoading = false;
          this.$root.$snackbar("error", { text: err });
          this.$root.$snackbarServerError(err, { level: "error" });
        });
    },
    async licenceDeactivate(id) {
      const answer = await this.$root.$confirm(
        this.$t("horses:licence.deactivate.modal.heading"),
        this.$t(
          "horses:horseProfilePage.licencesSection.horseSportLicenceDeactivate.confirmation"
        )
      );
      if (answer === true) {
        this.mutationId = id;
        try {
          await this.$apollo.mutate({
            mutation: gql`
              mutation horseSportLicenceDeactivate($id: ID!) {
                horseSportLicenceDeactivate(input: { id: $id }) {
                  horseSportLicence {
                    id
                    validFrom
                    validUntil
                    allowedMutations
                  }
                }
              }
            `,
            variables: { id: id }
          });
          this.$emit("refetch");
          // Doesn't work in refetchQueries for some reason. Maybe due to manual?
          this.$apollo.queries.licences.refetch();
          this.$root.$snackbar("success", { text: "Licence removed!" });
        } catch (err) {
          if (process.env.NODE_ENV === "development") console.error(err);
          this.$root.$snackbar("error", { text: err });
          this.$root.$snackbarServerError(err, { level: "error" });
        } finally {
          this.mutationId = null;
        }
      }
    },
    async licenceDiscard(item) {
      const answer = await this.$root.$confirm(
        "Discard?",
        `Are you sure you want to discard ${this.productCodenameToName(
          item.productCodename
        )}`
      );
      if (answer === true) {
        this.mutationId = item.id;
        try {
          await this.$apollo.mutate({
            mutation: gql`
              mutation horseSportLicenceDiscard($id: ID!) {
                horseSportLicenceDiscard(input: { id: $id }) {
                  horseSportLicence {
                    id
                  }
                }
              }
            `,
            variables: { id: item.id }
          });
          this.$emit("refetch");
          // Doesn't work in refetchQueries for some reason. Maybe due to manual?
          this.$apollo.queries.licences.refetch();
          this.$root.$snackbar("success", { text: "Discarded" });
        } catch (err) {
          if (process.env.NODE_ENV === "development") console.error(err);
          this.$root.$snackbar("error", { text: err });
          this.$root.$snackbarServerError(err, { level: "error" });
        } finally {
          this.mutationId = null;
        }
      }
    }
  }
};
</script>
